import * as React from 'react';

import { routes } from 'defs';
import i18n from 'helper/i18n';
import noop from 'helper/noop';

import ButtonBar, { ButtonBarProps } from 'components/molecules/ButtonBar';
import HealthGoal from 'components/molecules/HealthGoal';
import { calculatedBmi } from 'helper/bmi';
import validateBmi, { codes as bmiCodes } from 'helper/validator/bmi';

import Checkbox from 'components/atoms/Checkbox';
import Spacer from 'components/helper/Spacer';
import Agreement from 'components/molecules/Agreement';
import ElementList from 'components/molecules/ElementList';

import { HealthGoalItem } from 'components/molecules/HealthGoal/HealthGoal';
import { getHealthGoalTypesFromGoal, TGoal } from 'components/molecules/HealthGoalSelection/HealthGoalSelection';
import { AnamnesisStepResolver } from 'components/organisms/Anamnesis';
import { getHealthGoalRequiredAgreement } from 'components/organisms/Anamnesis/get-health-goal-required-agreement';
import { actionCreators as profileAC } from 'store/profile/profile.actionCreators';
import { ArgumentTypes } from 'utils/types';
import { GenderKeyType } from '../../../../types/GenderKey';

export type ReduceWeightTargetType = {
	recommendedReduceWeight: number | null;
	recommendedEnd: number | null;
	recommendedStart: number | null;
};

type AcceptHealthTargetProps = {
	actionButtonsActive: ButtonBarProps;
	actionButtonsInactive: ButtonBarProps;
	bmiValue: number;
	goals: HealthGoalItem[];
	selectedGoal?: TGoal;
	size?: number;
	weight?: number;
	age: number | null;
	gender: GenderKeyType | null;
	isPregnant?: boolean;
	reduceWeightTarget: ReduceWeightTargetType;
	isUpdatingAnamnesis?: boolean;
	onNavigationClick: (targetUrl: { href: string }) => void;
	onStepCompleted: (
		resolver: AnamnesisStepResolver<ArgumentTypes<typeof profileAC.doSaveAcceptHealthTarget>[0]>
	) => void;
	doSaveAcceptHealthTarget: typeof profileAC.doSaveAcceptHealthTarget;
};

type AcceptHealthTargetState = {
	weightInput?: number;
	initialWeightSet?: boolean;
	hasValidWeight: boolean;
	initialGoalSet?: boolean;
	userChangedWeightReduceTarget?: boolean;
	acceptedHealthGoalWarningOverweight: boolean;
	acceptedHealthGoalWarningUnderweight: boolean;
	acceptedHealthGoalWarningPregnant: boolean;
};

export default class AcceptHealthTarget extends React.Component<AcceptHealthTargetProps, AcceptHealthTargetState> {
	public static displayName = 'AnamnesisAcceptHealthTarget';
	public static defaultProps = {
		actionButtonsActive: {},
		actionButtonsInactive: {},
		goals: [],
		isPregnant: false,
		birthdate: null,
		gender: null,
		size: 0,
		weight: 0,
		onNavigationClick: noop,
		onStepCompleted: noop,
		doSaveAcceptHealthTarget: noop,
		reduceWeightTarget: {},
	};

	constructor(props: AcceptHealthTargetProps) {
		super(props);

		this.state = {
			hasValidWeight: false,
			acceptedHealthGoalWarningOverweight: false,
			acceptedHealthGoalWarningPregnant: false,
			userChangedWeightReduceTarget: false,
			acceptedHealthGoalWarningUnderweight: false,
		};
	}

	public onHandleButtonClicks = (type: string) => {
		const _this = this;
		const { onNavigationClick, onStepCompleted, reduceWeightTarget } = this.props;
		const recommendedReduceWeight = reduceWeightTarget.recommendedReduceWeight || 0;

		if (type === 'completed' && typeof onStepCompleted === 'function') {
			return onStepCompleted.apply(null, [
				{
					waitFor: 'savedAcceptHealthTarget',
					actionCreator: _this.props.doSaveAcceptHealthTarget,
					nextRoute: routes.anamnesis.selectDietaryHabits,
					data: {
						acceptedHealthGoalWarningUnderweight: _this.state.acceptedHealthGoalWarningUnderweight,
						acceptedHealthGoalWarningOverweight: _this.state.acceptedHealthGoalWarningOverweight,
						acceptedHealthGoalWarningPregnant: _this.state.acceptedHealthGoalWarningPregnant,
						healthGoal: getHealthGoalTypesFromGoal(this.props.selectedGoal),
						weightReduceGoal:
							parseFloat((_this.state.weightInput || 0).toString()) || recommendedReduceWeight,
					},
				},
			]);
		}

		if (typeof onNavigationClick === 'function') {
			type = type === 'completed' ? 'next' : type;
			onNavigationClick.apply(this, [routes.anamnesis.defineHealthTarget]);
		}
	};

	public onWeightChange = (weight: string, isValid: boolean) => {
		this.setState({
			weightInput: parseFloat(weight),
			hasValidWeight: !!isValid,
			userChangedWeightReduceTarget: true,
		});
	};

	public onAcceptPregnant = (accepted: boolean) => {
		this.setState({
			acceptedHealthGoalWarningPregnant: accepted,
		});
	};

	public onAcceptOverweight = (accepted: boolean) => {
		this.setState({
			acceptedHealthGoalWarningOverweight: accepted,
		});
	};

	public onAcceptUnderweight = (accepted: boolean) => {
		this.setState({
			acceptedHealthGoalWarningUnderweight: accepted,
		});
	};

	get calculatedBmi(): number {
		if (this.isLoseWeight && this.state.weightInput) {
			return calculatedBmi(this.props.weight, this.props.size, this.state.weightInput, true, true);
		}

		return this.props.bmiValue;
	}

	get requireCheckbox(): boolean {
		return !!(this.calculatedBmi >= 30);
	}

	get bmiIsValidToContinue() {
		return this.calculatedBmi > 18.5;
	}

	get isGettingFit() {
		return Boolean(this.props.selectedGoal === 'running');
	}

	get isGettingHealthier() {
		return Boolean(this.props.selectedGoal === 'healthier');
	}

	get isLoseWeight() {
		return Boolean(this.props.selectedGoal === 'fruits');
	}

	get bmiValidationResult() {
		return validateBmi({
			calculatedBmi: this.calculatedBmi,
			calculateInitialBmi: this.props.bmiValue,
			isPregnant: !!this.props.isPregnant,
			isGettingFit: this.isGettingFit,
			isGettingHealthier: this.isGettingHealthier,
			acceptedWarningPregnant: this.state.acceptedHealthGoalWarningPregnant,
			acceptedWarningUnderweight: this.state.acceptedHealthGoalWarningUnderweight,
			acceptedWarningOverweight: this.state.acceptedHealthGoalWarningOverweight,
			hasState: !!this.state,
			hasGoal: !!this.props.selectedGoal,
			hasValidWeight: this.state.hasValidWeight,
			bmiIsValidToContinue: this.bmiIsValidToContinue,
			hasSuggestedReduceGoal: typeof this.props.reduceWeightTarget.recommendedReduceWeight === 'number',
			isCustomInput: !!this.state.userChangedWeightReduceTarget,
		});
	}

	public renderButtons() {
		const { actionButtonsInactive, actionButtonsActive, isUpdatingAnamnesis } = this.props;
		let actionButtons: Object;

		switch (this.bmiValidationResult) {
			case bmiCodes.GETTING_HEALTHIER: {
				actionButtons = actionButtonsActive;
				break;
			}
			case bmiCodes.FEMALE_PREGNANT_GETFIT_WARN: {
				actionButtons = actionButtonsActive;
				break;
			}
			case bmiCodes.FEMALE_PREGNANT_GETFIT: {
				actionButtons = actionButtonsInactive;
				break;
			}
			case bmiCodes.UNDERWEIGHT:
			case bmiCodes.OVERWEIGHT:
			case bmiCodes.NO_GOAL:
			case bmiCodes.REDUCE_WEIGHT_INVALID_WEIGHT_OR_BMI: {
				actionButtons = actionButtonsInactive;
				break;
			}
			default: {
				actionButtons = actionButtonsActive;
			}
		}

		return (
			<ButtonBar
				{...(actionButtons as ButtonBarProps)}
				loading={isUpdatingAnamnesis}
				handleButtonClicks={this.onHandleButtonClicks}
			/>
		);
	}

	public renderAgreement() {
		const { isPregnant, selectedGoal, weight, size, bmiValue } = this.props;
		const { weightInput } = this.state;
		const requiredArgreement = getHealthGoalRequiredAgreement({
			isPregnant,
			selectedGoal,
			weightInput,
			weight,
			size,
			bmiValue,
		});

		if (requiredArgreement === 'pregnant') {
			return (
				<div>
					<Spacer size="xl" />
					<Agreement
						title={i18n('react.impulsCoach.components.anamnesis.acceptHealthTarget.pregnantWarning.title')}
						text={i18n('react.impulsCoach.components.anamnesis.acceptHealthTarget.pregnantWarning.text')}
					>
						<ElementList>
							<Checkbox
								tag="li"
								id="anamnesis-accept-pregnant"
								name="anamnesis-accept-pregnant"
								label={i18n(
									'react.impulsCoach.components.anamnesis.acceptHealthTarget.pregnantWarning.checkboxLabel'
								)}
								onChange={this.onAcceptPregnant}
								modifier="white"
								disabled={this.props.isUpdatingAnamnesis}
							/>
						</ElementList>
					</Agreement>
				</div>
			);
		}

		if (requiredArgreement === 'underweight') {
			return (
				<div>
					<Spacer size="xl" />
					<Agreement
						title={i18n(
							'react.impulsCoach.components.anamnesis.acceptHealthTarget.underweightWarning.title'
						)}
						text={i18n('react.impulsCoach.components.anamnesis.acceptHealthTarget.underweightWarning.text')}
					>
						<ElementList>
							<Checkbox
								tag="li"
								id="anamnesis-accept-underweight"
								name="anamnesis-accept-underweight"
								label={i18n(
									'react.impulsCoach.components.anamnesis.acceptHealthTarget.underweightWarning.checkboxLabel'
								)}
								onChange={this.onAcceptUnderweight}
								modifier="white"
								disabled={this.props.isUpdatingAnamnesis}
							/>
						</ElementList>
					</Agreement>
				</div>
			);
		}

		return (
			<div style={requiredArgreement === 'overweight' ? {} : { display: 'none' }}>
				<Spacer size="xl" />
				<Agreement
					title={i18n('react.impulsCoach.components.anamnesis.acceptHealthTarget.overweightWarning.title')}
					text={i18n('react.impulsCoach.components.anamnesis.acceptHealthTarget.overweightWarning.text')}
				>
					<ElementList>
						<Checkbox
							tag="li"
							id="anamnesis-accept-overweight"
							name="anamnesis-accept-overweight"
							label={i18n(
								'react.impulsCoach.components.anamnesis.acceptHealthTarget.overweightWarning.checkboxLabel'
							)}
							onChange={this.onAcceptOverweight}
							modifier="white"
							disabled={this.props.isUpdatingAnamnesis}
						/>
					</ElementList>
				</Agreement>
			</div>
		);
	}

	public renderHealthGoal() {
		const { goals, weight, size, isPregnant, reduceWeightTarget, isUpdatingAnamnesis, gender, age } = this.props;

		if (
			!reduceWeightTarget ||
			typeof reduceWeightTarget.recommendedReduceWeight !== 'number' ||
			!this.props.selectedGoal
		) {
			return <div />;
		}

		return (
			<HealthGoal
				selectedGoal={this.props.selectedGoal}
				goals={goals}
				weight={weight}
				age={age}
				gender={gender}
				size={size}
				bmiValue={this.props.bmiValue}
				isPregnant={isPregnant}
				reduceWeightTarget={reduceWeightTarget}
				onInputChange={this.onWeightChange}
				disabled={isUpdatingAnamnesis}
			/>
		);
	}

	public render() {
		return (
			<div>
				{this.renderHealthGoal()}
				{this.renderAgreement()}
				<div className="o-anamnesis__button-bar">{this.renderButtons()}</div>
			</div>
		);
	}
}
